import { describeResult } from '../lib/display.ts';
import { formatAudioEvent, judgePlayback } from '../lib/playbackReport.ts';
import { audioTexts } from '../lib/lessonSteps.ts';
import { audioIds, audioUrl } from '../speech/audioIds.ts';
import { describeMediaError, levelVerdict, peakPercent } from '../lib/mediaErrors.ts';
import { errorFromRecognition, getRecognitionCtor } from '../speech/assessors.ts';
import { AssessError } from '../speech/types.ts';
import { probePlayback } from './audioProbe.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';

interface DiagRecognition {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onstart: (() => void) | null;
  onaudiostart: (() => void) | null;
  onsoundstart: (() => void) | null;
  onspeechstart: (() => void) | null;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  abort(): void;
}

const MIC_TEST_MS = 6000;
const REC_TEST_MS = 9000;

/** Panel diagnostyczny: co dokładnie jest, a czego brakuje (mikrofon, rozpoznawanie mowy, Azure). */
export function renderDiagnostics(ctx: AppCtx): HTMLElement {
  const out = h('ul', { class: 'diag', 'data-testid': 'diag-out' });
  const add = (ok: boolean | null, text: string) => {
    const li = h('li', { class: ok === null ? 'info' : ok ? 'ok' : 'bad' }, `${ok === null ? 'ℹ️' : ok ? '✅' : '❌'} ${text}`);
    out.append(li);
    return li;
  };
  let running = false;
  const runBtn = button('🔧 Diagnostyka mikrofonu i oceny', () => void run(), { class: 'btn ghost', testid: 'diag-run' });

  async function testLevel(): Promise<void> {
    let stream: MediaStream | null = null;
    let audioCtx: AudioContext | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const track = stream.getAudioTracks()[0];
      add(true, `Mikrofon uruchomiony: ${track?.label || '(bez nazwy)'}`);
      const inputs = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'audioinput').length;
      add(null, `Liczba wejść audio w systemie: ${inputs}`);

      const bar = h('progress', { max: 100, value: 0, 'data-testid': 'diag-level' });
      add(null, `Test poziomu: MÓW GŁOŚNO przez ${MIC_TEST_MS / 1000} sekund (np. „hello, my name is…”). `).append(bar);
      audioCtx = new AudioContext();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      let peak = 0;
      const until = Date.now() + MIC_TEST_MS;
      while (Date.now() < until) {
        analyser.getByteTimeDomainData(data);
        const now = peakPercent(data);
        peak = Math.max(peak, now);
        bar.value = now;
        await new Promise((r) => setTimeout(r, 50));
      }
      bar.remove();
      const v = levelVerdict(peak);
      add(v.ok, v.text);
    } catch (e) {
      const name = e instanceof Error ? e.name : String(e);
      add(false, `${describeMediaError(name)} (${name})`);
    } finally {
      stream?.getTracks().forEach((t) => t.stop());
      void audioCtx?.close();
    }
  }

  /** Bezpośredni test Azure (bez zapasowych metod): pokazuje pełny komunikat błędu, jeśli coś nie gra. */
  async function testAzure(): Promise<void> {
    const azure = ctx.chain().assessors.find((a) => a.name === 'azure');
    if (!azure) return;
    add(null, 'Test Azure: powiedz po angielsku „This is water.” (do ok. 10 sekund).');
    try {
      const r = await azure.assess('This is water.');
      add(true, `Azure ocenił wypowiedź: rozpoznano „${r.recognizedText ?? ''}”, dokładność ${Math.round(r.accuracy)}, kompletność ${Math.round(r.completeness)}, płynność ${Math.round(r.fluency ?? 0)}.`);
      add(null, `Szczegóły: ${describeResult(r)}`);
    } catch (e) {
      add(false, `Azure zgłosił błąd: ${e instanceof AssessError ? e.code : 'inny'} — ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  /** Jedno nasłuchiwanie przez rozpoznawanie mowy przeglądarki, z dziennikiem zdarzeń — pokazuje, na którym etapie coś się urywa. */
  function testRecognition(): Promise<void> {
    const Ctor = getRecognitionCtor() as unknown as (new () => DiagRecognition) | null;
    if (!Ctor) return Promise.resolve();
    return new Promise((resolve) => {
      add(null, `Test rozpoznawania mowy: powiedz po angielsku „hello, my name is…” (do ${REC_TEST_MS / 1000} sekund).`);
      const events: string[] = [];
      let transcript = '';
      let error = '';
      const rec = new Ctor();
      rec.lang = 'en-US';
      rec.interimResults = true;
      rec.maxAlternatives = 1;
      rec.continuous = false;
      rec.onstart = () => events.push('start');
      rec.onaudiostart = () => events.push('audiostart');
      rec.onsoundstart = () => events.push('soundstart');
      rec.onspeechstart = () => events.push('speechstart');
      rec.onresult = (e) => {
        const first = e.results[e.results.length - 1]?.[0];
        if (first) transcript = first.transcript;
        events.push('result');
      };
      rec.onerror = (e) => {
        error = e.error;
        events.push(`error:${e.error}`);
      };
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        add(null, `Zdarzenia: ${events.join(' → ') || '(żadne)'}`);
        if (transcript) add(true, `Rozpoznano: „${transcript}”`);
        else if (error) add(false, `Rozpoznawanie mowy zgłosiło błąd: ${errorFromRecognition(error).code} (${error}).`);
        else if (!events.includes('audiostart')) add(false, 'Przeglądarka zakończyła nasłuch, zanim uruchomiła mikrofon (brak audiostart). To dokładnie objaw „od razu się wyłącza”.');
        else add(false, 'Mikrofon działał, ale nic nie rozpoznano (cisza albo zbyt cicho).');
        resolve();
      };
      rec.onend = finish;
      const timer = setTimeout(() => {
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
        finish();
      }, REC_TEST_MS);
      try {
        rec.start();
      } catch (e) {
        error = 'start-failed';
        events.push(`wyjątek: ${e instanceof Error ? e.message : String(e)}`);
        finish();
      }
    });
  }

  async function run(): Promise<void> {
    if (running) return;
    running = true;
    runBtn.disabled = true;
    out.replaceChildren();
    try {
      add(null, `Przeglądarka: ${navigator.userAgent}`);
      add(window.isSecureContext, window.isSecureContext ? 'Strona w bezpiecznym kontekście (HTTPS lub localhost) — mikrofon dozwolony.' : 'Strona NIE jest w HTTPS ani localhost — przeglądarka zablokuje mikrofon.');
      const rec = getRecognitionCtor();
      add(rec !== null, rec ? 'Rozpoznawanie mowy w przeglądarce: dostępne.' : 'Rozpoznawanie mowy w przeglądarce: BRAK (np. Firefox) — potrzebny Azure albo inna przeglądarka (Chrome, Edge).');
      const names = ctx.chain().assessors.map((a) => a.name);
      add(names.includes('azure'), names.includes('azure') ? 'Azure: skonfigurowany (token z /api/token działa).' : `Azure: niedostępny — ${ctx.chain().azureDetail ?? 'brak kluczy w .env lub /api/token nie odpowiada'}.`);

      if (!navigator.mediaDevices?.getUserMedia) {
        add(false, 'Ta przeglądarka nie udostępnia getUserMedia (mikrofon).');
        return;
      }
      await testLevel();
      await testAzure();
      await testRecognition();
      add(null, 'Koniec diagnostyki. Zrób zrzut ekranu tej listy i prześlij.');
    } finally {
      running = false;
      runBtn.disabled = false;
    }
  }

  /** Test odtwarzania nagrania w tej przeglądarce + dziennik odtwarzacza aplikacji (kto i kiedy przerwał dźwięk). */
  async function runAudioTest(): Promise<void> {
    if (running) return;
    running = true;
    runBtn.disabled = audioBtn.disabled = true;
    out.replaceChildren();
    try {
      add(null, `Przeglądarka: ${navigator.userAgent}`);
      add(null, `Adres strony: ${location.origin}`);
      const events = ctx.player.log?.() ?? [];
      if (events.length > 0) {
        add(null, 'Dziennik odtwarzania w aplikacji (ostatnie zdarzenia; „PRZERWANE” = dźwięk zatrzymany przed końcem, z powodem):');
        const t0 = events[0]?.at ?? Date.now();
        for (const e of events.slice(-14)) add(e.what === 'cut' || e.what === 'error' || e.what === 'blocked' ? false : null, formatAudioEvent(e, t0));
      } else add(null, 'Dziennik odtwarzania jest pusty (nic jeszcze nie grało w tej sesji).');
      // próbne nagranie: pierwsze zdanie pierwszej lekcji kursu (nagrania „dni” zniknęły z aplikacji w patchu 49)
      const first = ctx.course?.lessons[0];
      const clip = first ? audioTexts(first).find((t) => t.kind !== 'target') : undefined;
      if (!clip) return;
      const id = audioIds.lesson(clip.id);
      add(null, `Test nagrania „${id}” — usłyszysz je teraz; posłuchaj, czy jest całe.`);
      try {
        for (const line of judgePlayback(await probePlayback(audioUrl(id), id))) add(line.ok, line.text);
      } catch (e) {
        add(false, `Test nie udał się: ${e instanceof Error ? e.message : String(e)}`);
      }
      add(null, 'Koniec testu. Zrób zrzut ekranu tej listy i prześlij.');
    } finally {
      running = false;
      runBtn.disabled = audioBtn.disabled = false;
    }
  }
  const audioBtn = button('🔊 Test nagrania i dziennik dźwięku', () => void runAudioTest(), { class: 'btn ghost', testid: 'diag-audio' });

  return h('div', { class: 'diag-box' }, runBtn, audioBtn, out);
}
